import React, { useState, ChangeEvent, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  TablePagination,
  Chip,
  Skeleton,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import { withAuth } from "../../hoc";
import DietPlanDrawer from "./DrawerPlan";
import { MemberResponse } from "../../services/MembersService";
import {
  capitalizeFirstLetter,
  getMemberType,
  getReadableDateString,
} from "../../utils/commons";
import {
  DietPlanFace,
  GetDietPlanRequests,
} from "../../services/DietplanServices";

const Dietplans = () => {
  const [tabValue, setTabValue] = useState<
    "ALL" | "REQUESTED" | "IN_PROGRESS" | "ADDRESSED"
  >("ALL");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [selectedRequest, setSelectedRequest] = useState<any>("");
  const [totalPages, setTotalPages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [dietRequests, setDietRequests] = useState<DietPlanFace[]>([]);
  const [deitRequestsCount, setDeitRequestsCount] = useState<number>(0);
  const theme = useTheme();
  useEffect(() => {
    fetchDeitRequests(1);
  }, [tabValue]);

  useEffect(() => {
    fetchDeitRequests(currentPage);
  }, [currentPage]);

  const onReloadReqeusts = async () => {
    await fetchDeitRequests(1);
  };

  const fetchDeitRequests = async (page: number) => {
    setLoading(true);
    setCurrentPage(page);
    try {
      const result = await GetDietPlanRequests({
        page,
        size: rowsPerPage,
      });
      setDeitRequestsCount(result.totalElements);
      setDietRequests(result.content);
      setTotalPages(result.totalPages);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };
  const handleTabChange = (event: any, newValue: any) => {
    setTabValue(newValue);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleClose = () => {
    setDrawerOpen(false);
  };

  const handleOpen = () => {
    setDrawerOpen(true);
  };

  const filteredPlans = dietRequests.filter(
    (plan) =>
      tabValue === "ALL" ||
      plan.requestStatus.toLowerCase() === tabValue.toLowerCase()
  );

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "90.5vh",
        padding: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            marginBottom: theme.spacing(2),
            color: theme.palette.text.primary,
          }}
        >
          Diet Plans
        </Typography>
      </Box>
      <DietPlanDrawer
        open={drawerOpen}
        onClose={handleClose}
        selectedRequest={selectedRequest}
        onReload={onReloadReqeusts}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 2,
        }}
      >
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab value="ALL" label="All" sx={{ minWidth: "initial" }} />
          {/* <Tab
            value="REQUESTED"
            label="Requested"
            sx={{ minWidth: "initial" }}
          /> */}
          {/* <Tab
            value="IN_PROGRESS"
            label="InProgress"
            sx={{ minWidth: "initial" }}
          /> */}
          {/* <Tab
            value="ADDRESSED"
            label="Approved"
            sx={{ minWidth: "initial" }}
          /> */}
        </Tabs>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton aria-label="filter" size="small" sx={{ marginRight: 1 }}>
            <FilterListIcon />
          </IconButton>
          <TextField
            placeholder="Search"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
      <TableHeaderComponent />
      <Box
        sx={{
          height: "100%",
          overflowY: "auto",
          scrollbarWidth: "none",
        }}
      >
        <TableContainer>
          <Table>
            <TableBody>
              {loading && (
                <>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <LoadingSkeletonRow key={index} />
                  ))}
                </>
              )}

              {!loading && filteredPlans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No data found
                  </TableCell>
                </TableRow>
              )}
              {!loading && filteredPlans.length > 0 && (
                <>
                  {filteredPlans.map((plan) => (
                    <TableRow
                      onClick={() => {
                        setSelectedRequest(plan);
                        handleOpen();
                      }}
                      key={plan.id}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <TableCell
                        sx={{ width: 200, maxWidth: 200, minWidth: 200 }}
                        align="left"
                      >
                        {capitalizeFirstLetter(plan.user?.firstName) +
                          " " +
                          capitalizeFirstLetter(plan.user?.lastName)}
                      </TableCell>
                      <TableCell
                        sx={{ width: 100, maxWidth: 100, minWidth: 100 }}
                        align="center"
                      >
                        {capitalizeFirstLetter(plan.user?.gender)}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ width: 130, maxWidth: 130, minWidth: 130 }}
                      >
                        {getMemberType(plan.user?.memberType)}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ width: 130, maxWidth: 130, minWidth: 130 }}
                      >
                        {getReadableDateString(plan.updatedAt)}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ width: 130, maxWidth: 130, minWidth: 130 }}
                      >
                        {getReadableDateString(plan.createdAt)}
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{
                          width: 150,
                          maxWidth: 150,
                          minWidth: 150,
                        }}
                        style={{ fontSize: "14px", fontWeight: "bold" }}
                      >
                        {getDietStatusChip(plan.requestStatus)}
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "auto",
          marginBottom: 0,
          color: theme.palette.text.secondary,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body2" sx={{ marginRight: 1 }}>
            Users Count: <b>{deitRequestsCount}</b>&nbsp;,
          </Typography>
          <Typography variant="body2">
            Page <b>{currentPage}</b> of {totalPages}
          </Typography>
        </Box>

        <Box>
          <Button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <Button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default withAuth(Dietplans);

const TableHeaderComponent = () => {
  const theme = useTheme();
  return (
    <>
      <TableContainer sx={{ height: 100 }}>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: theme.palette.info.light,
              }}
            >
              <TableCell
                sx={{ width: 200, maxWidth: 200, minWidth: 200 }}
                align="center"
              >
                User Name
              </TableCell>
              <TableCell
                sx={{ width: 100, maxWidth: 100, minWidth: 100 }}
                align="center"
              >
                Gender
              </TableCell>
              <TableCell
                sx={{ width: 130, maxWidth: 130, minWidth: 130 }}
                align="center"
              >
                MemberType
              </TableCell>
              <TableCell
                sx={{ width: 130, maxWidth: 130, minWidth: 130 }}
                align="center"
              >
                Last Updated
              </TableCell>
              <TableCell
                sx={{ width: 130, maxWidth: 130, minWidth: 130 }}
                align="center"
              >
                Created At
              </TableCell>
              <TableCell
                sx={{ width: 150, maxWidth: 150, minWidth: 150 }}
                align="center"
              >
                Status
              </TableCell>
            </TableRow>
          </TableHead>
        </Table>
      </TableContainer>
    </>
  );
};

const getDietStatusChip = (status: string) => {
  const commonStyles = {
    fontSize: "14px",
    fontWeight: "bold",
    fontFamily: "inherit",
    height: "32px",
    width: 170,
    display: "flex",
    alignItems: "center",
    margin: "auto",
    borderRadius: "16px",
  };

  switch (status) {
    case "REQUESTED":
      return (
        <Chip
          label="Requested"
          style={{
            ...commonStyles,
            backgroundColor: "gray", // Amber color
            color: "#FFF", // Black text
          }}
        />
      );
    case "IN_PROGRESS":
      return (
        <Chip
          label="In Progress"
          style={{
            ...commonStyles,
            backgroundColor: "#FFC107", // Blue color
            color: "#FFF", // White text
          }}
        />
      );
    case "ADDRESSED":
      return (
        <Chip
          label="Addressed"
          style={{
            ...commonStyles,
            backgroundColor: "#4CAF50", // Green color
            color: "#FFF", // White text
          }}
        />
      );
    default:
      return null;
  }
};

const LoadingSkeletonRow = () => {
  const theme = useTheme();

  return (
    <TableRow
      sx={{
        cursor: "pointer",
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
      }}
    >
      <TableCell sx={{ width: 200, maxWidth: 200, minWidth: 200 }} align="left">
        <Skeleton variant="text" width={150} />
      </TableCell>
      <TableCell
        sx={{ width: 100, maxWidth: 100, minWidth: 100 }}
        align="center"
      >
        <Skeleton variant="text" width={100} />
      </TableCell>
      <TableCell
        align="center"
        sx={{ width: 130, maxWidth: 130, minWidth: 130 }}
      >
        <Skeleton variant="text" width={130} />
      </TableCell>
      <TableCell
        align="center"
        sx={{ width: 130, maxWidth: 130, minWidth: 130 }}
      >
        <Skeleton variant="text" width={130} />
      </TableCell>
      <TableCell
        align="center"
        sx={{ width: 130, maxWidth: 130, minWidth: 130 }}
      >
        <Skeleton variant="text" width={130} />
      </TableCell>
      <TableCell
        align="center"
        sx={{
          width: 150,
          maxWidth: 150,
          minWidth: 150,
        }}
        style={{ fontSize: "14px", fontWeight: "bold" }}
      >
        <Skeleton variant="text" width={150} />
      </TableCell>
    </TableRow>
  );
};
